import React, {useEffect, useRef, useState} from "react";
import {AutoComplete, Button, Checkbox, Input, Pagination, Tag} from "antd";
import {Form} from "react-bootstrap";
import styled from "styled-components";
import loupe from "../../images/loupe.svg"
import usePortalService from '../../services/PortalService';
import { AdminNavbar } from "../../Components/Navbars";
import { VacancyCardEmployer } from "../../Components/VacancyCards";
import type { DefaultOptionType } from "antd/es/select";
import { Vacancy } from "../../types";

const Styles = styled.div`
  .vacancies-container {
    width: 100%;
  }

  .inputs-container {
    overflow-x: hidden;
    text-overflow: ellipsis;
    overflow-y: auto;
    padding-right: 10px;
    scroll-padding-right: 10px;
    width: 100%;

    scrollbar-width: thin;
    scrollbar-color: #D1D6DC transparent;
  }

  .inputs-container::-webkit-scrollbar-thumb {
    background-color: #D1D6DC;
    border-radius: 10px;
  }

  .inputs-container::-webkit-scrollbar {
    width: 6px;
  }

  .ant-select-selector {
    height: 40px !important;
    padding: 10px 12px 10px 12px !important;
    border-radius: 2px;
    border: 1px solid #D1D6DC !important;
  }

  .ant-select-selector:hover {
    border-color: #56ADF3 !important;
  }

  .ant-select-selector:focus {
    box-shadow: 0px 0px 4px 0px #0E6AED80 !important;
  }

  .ant-select-selection-search {
    display: flex;
    flex-direction: column;
    justify-content: center;
  }
  
  .ant-checkbox-wrapper :nth-child(2) {
    font-weight: 400;
  }

  @media screen and (max-width: 768px) {
    .filter-container {
      position: static;
      width: 100%;
    }

    .filter-buttons {
      padding-right: 0px;
    }

    .inputs-container {
      padding-right: 0px;
    }
  }
`

const Checkboxes = styled.div`
  margin-bottom: 20px;
  display: flex;
  flex-direction: column;
  
  .checkboxes-group {
    display: flex;
    flex-direction: column;
    gap: 4px;
  }
`

const InputForSearch = styled(Input)`
  max-width: 550px;
  width: 100%;
  border: 1px solid #D1D6DC;
  border-radius: 2px;
  padding: 0 8px;
  &:hover {
    border-color: #56ADF3 !important;
  }
  
  &:focus {
    box-shadow: 0px 0px 4px 0px #0E6AED80 !important;
  }
  
  & ::placeholder {
    font-size: 14px;
    font-weight: 400;
    line-height: 22px;
    color : #A9B3BC;
    vertical-align: bottom;
  }
  
  input {
    padding-bottom: 10px !important;
    padding-top: 8px !important;
  }

  @media screen and (max-width: 768px) {
    max-width: none;
  }
`

const StyledTag = styled(Tag)`
  max-width: 230px;
  display: inline-flex;
  gap: 0;
  
  span:nth-child(1) {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`

let nextTagId = 0;
let selectedPage: number;

export const AdminVacancies = () => {
  const {getAllVacancyCities, getAllVacancyRegions} = usePortalService();

  const inputsRef = useRef<HTMLDivElement>(null)
  const buttonsRef = useRef<HTMLDivElement>(null)

  const { listVacancies } = usePortalService();

  const [searchText, setSearchText] = useState('')

  const [city, setCity] = useState('');
  const [selectedCities, setSelectedCities] = useState<Array<{ id: number; name: string }>>([]);
  const [isCitiesLoaded, setIsCitiesLoaded] = useState(false);
  const [citiesList, setCitiesList] = useState<{ value: string }[]>([]);

  const [region, setRegion] = useState('');
  const [selectedRegions, setSelectedRegions] = useState<Array<{ id: number; name: string }>>([]);
  const [isRegionsLoaded, setIsRegionsLoaded] = useState(false);
  const [regionsList, setRegionsList] = useState<{ value: string }[]>([]);

  const [isSearchingForTraineeship, setIsSearchingForTraineeship] = useState(false)
  const [isSearchingForJob, setIsSearchingForJob] = useState(false)

  const [isHighEducation, setIsHighEducation] = useState(false)
  const [isSecondaryVocationalEducation, setIsSecondaryVocationalEducation] = useState(false)

  const [page, setPage] = useState(1);
  const vacancyPageSize = 20;
  const [totalCount, setTotalCount] = useState(0);

  const [vacancies, setVacancies] = useState<Vacancy[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);

  useEffect(() => {
    window.addEventListener("scroll", onPositionChange)
    window.addEventListener("resize", onPositionChange)

    const buttons = buttonsRef.current;
    const inputs = inputsRef.current;

    if (buttons && inputs) {
      if (buttons.getBoundingClientRect().top - inputs.getBoundingClientRect().top < 0) {
        buttons.style.position = "sticky"
        return;
      }

      inputs.style.maxHeight = (buttons.getBoundingClientRect().top - inputs.getBoundingClientRect().top) + 1 + "px"

      if (inputs.scrollHeight > inputs.clientHeight){
        buttons.style.position = "fixed"
      } else {
        buttons.style.position = "sticky"
      }
    }

    loadVacancies();
  }, []);

  const loadVacancies = () => {
    selectedPage = 0
    const thereAreFilters = sessionStorage.getItem('vacanciesFilters');
    if (thereAreFilters !== null) {
      const loadedRegions = JSON.parse(sessionStorage.getItem('vacanciesFiltersRegions') || '[]');
      if (loadedRegions) {
        setSelectedRegions(loadedRegions);
      }
      const loadedCities = JSON.parse(sessionStorage.getItem('vacanciesFiltersCities') || '[]');
      if (loadedCities) {
        setSelectedCities(loadedCities);
      }
      const loadedTraineeship = JSON.parse(sessionStorage.getItem('isSearchingForTraineeship') || 'false');
      if (loadedTraineeship) {
        setIsSearchingForTraineeship(loadedTraineeship);
      }
      const loadedJob = JSON.parse(sessionStorage.getItem('isSearchingForJob') || 'false');
      if (loadedJob) {
        setIsSearchingForJob(loadedJob);
      }
      const loadedHighEducation = JSON.parse(sessionStorage.getItem('isHighEducation') || 'false');
      if (loadedHighEducation) {
        setIsHighEducation(loadedHighEducation);
      }
      const loadedVocationalEducation = JSON.parse(sessionStorage.getItem('isSecondaryVocationalEducation') || 'false');
      if (loadedVocationalEducation) {
        setIsSecondaryVocationalEducation(loadedVocationalEducation);
      }
      const regions = loadedRegions.map((item: {name: string}) => (
        item.name
      ))
      const cities = loadedCities.map((item: {name: string}) => (
          item.name
      ))
      const text = sessionStorage.getItem('searchText');
      if (text) {
        setSearchText(text);
      }
      listVacancies({
        offset: 0,
        limit: vacancyPageSize,
        sortBy: "string",
        sortOrder: 0,
        filter: {
          regions,
          cities,
          checkboxes: {
            items: [
              {
                value: 0,
                isChecked: loadedHighEducation
              },
              {
                value: 1,
                isChecked: loadedVocationalEducation
              },
              {
                value: 2,
                isChecked: loadedTraineeship
              },
              {
                value: 3,
                isChecked: loadedJob
              }
            ]
          },
          text
        }
      }).then(result => {
        setVacancies(result.vacancies.items)
        setTotalCount(result.vacancies.totalCount)
        setIsLoaded(true)
      })
    } else {
      listVacancies({
        offset: 0,
        limit: vacancyPageSize,
        sortBy: "string",
        sortOrder: 0
      }).then(result => {
        setVacancies(result.vacancies.items)
        setTotalCount(result.vacancies.totalCount)
        setIsLoaded(true)
      })
    }
    onInnerSizeIncreasing();
  }

  const findVacancies = async (e?: React.MouseEvent<HTMLElement>) => {
      setIsLoaded(false);
      const offset = (selectedPage-1)*vacancyPageSize;
      const regions = selectedRegions.map(item => (
        item.name
      ))
      const cities = selectedCities.map(item => (
          item.name
      ))
      const result = await listVacancies({
          offset,
          limit: vacancyPageSize,
          sortBy: "string",
          sortOrder: 0,
          filter: {
            regions,
            cities,
            checkboxes: {
              items: [
                {
                  value: 0,
                  isChecked: isHighEducation
                },
                {
                  value: 1,
                  isChecked: isSecondaryVocationalEducation
                },
                {
                  value: 2,
                  isChecked: isSearchingForTraineeship
                },
                {
                  value: 3,
                  isChecked: isSearchingForJob
                }
              ]
            },
            text : searchText
          }
      });
      if (offset > result.vacancies.totalCount && result.vacancies.totalCount != 0) {
          selectedPage = Math.ceil(result.vacancies.totalCount/vacancyPageSize);
          setPage(selectedPage)
          findVacancies(e);
      } else {
          setVacancies(result.vacancies.items)
          setTotalCount(result.vacancies.totalCount)
      }
      setIsLoaded(true);
  }

  const saveFilters = () => {
    sessionStorage.setItem('vacanciesFilters', 'true')
    sessionStorage.setItem('vacanciesFiltersRegions', JSON.stringify(selectedRegions))
    sessionStorage.setItem('vacanciesFiltersCities', JSON.stringify(selectedCities))
    sessionStorage.setItem('isSearchingForTraineeship', JSON.stringify(isSearchingForTraineeship))
    sessionStorage.setItem('isSearchingForJob', JSON.stringify(isSearchingForJob))
    sessionStorage.setItem('isHighEducation', JSON.stringify(isHighEducation))
    sessionStorage.setItem('isSecondaryVocationalEducation', JSON.stringify(isSecondaryVocationalEducation))
    sessionStorage.setItem('searchText', searchText)
  }

  const pageHandler = (pageNumber: number) => {
    setPage(pageNumber);
    selectedPage = pageNumber;
    findVacancies();
  };

  const addTag = (e: string, list: Array<{ id: number; name: string }>, setList: React.Dispatch<React.SetStateAction<Array<{ id: number; name: string }>>>, setField: React.Dispatch<React.SetStateAction<string>>) => {
    const ind = list.findIndex((a) => a.name === e);
    if (ind === -1) {
      nextTagId++;
      setList([...list, { id: nextTagId, name: e }]);
      setField('');
      onInnerSizeIncreasing();
    }
  };
  
  const closeTag = (e: React.MouseEvent<HTMLElement>, name: string, list: Array<{ id: number; name: string }>, setList: React.Dispatch<React.SetStateAction<Array<{ id: number; name: string }>>>) => {
    setList(
      list.filter(a => a.name !== name)
    )

    if (buttonsRef.current) buttonsRef.current.style.position = "sticky"
  }

  const getCities = async (e: React.MouseEvent<HTMLElement>) => {
    if (!isCitiesLoaded) {
      e.preventDefault();
      const result = await getAllVacancyCities();
      if (result.success) {
          const resArr = result.cities.map((item: string) => ({ value: item }));
          setCitiesList(resArr);
          setIsCitiesLoaded(true)
      }
    }
  }

  const cityHandler = (e: string) => {
    setCity(e);
  }

  const getRegions = async (e: React.MouseEvent<HTMLElement>) => {
    if (!isRegionsLoaded) {
      e.preventDefault();
      const result = await getAllVacancyRegions();
      if (result.success) {
          const resArr = result.regions.map((item: string) => ({ value: item }));
          setRegionsList(resArr);
          setIsRegionsLoaded(true)
      }
    }
  }

  const regionHandler = (e: string) => {
    setRegion(e)
  }

  const findHandler = () => {
    findVacancies();
    saveFilters();
  }

  const resetHandler = () => {
    setCity('')
    setSelectedCities([])
    setRegion('')
    setSelectedRegions([])
    setIsSecondaryVocationalEducation(false)
    setIsHighEducation(false)
    setIsSearchingForTraineeship(false)
    setIsSearchingForJob(false)
    if (buttonsRef.current) buttonsRef.current.style.position = "sticky"
    sessionStorage.removeItem('vacanciesFilters');
    sessionStorage.removeItem('vacanciesFiltersRegions');
    sessionStorage.removeItem('vacanciesFiltersCities');
    sessionStorage.removeItem('isSearchingForTraineeship');
    sessionStorage.removeItem('isSearchingForJob');
    sessionStorage.removeItem('isHighEducation');
    sessionStorage.removeItem('isSecondaryVocationalEducation');
  }

  const onPositionChange = () => {
    const buttons = buttonsRef.current;
    const inputs = inputsRef.current;

    if (!buttons || !inputs) return;

    if (buttons.getBoundingClientRect().top - inputs.getBoundingClientRect().top < 0) {
      buttons.style.position = "sticky";
      return;
    }

    inputs.style.maxHeight = (buttons.getBoundingClientRect().top - inputs.getBoundingClientRect().top) + 1 + "px"

    if (inputs.scrollHeight > inputs.clientHeight){
      buttons.style.position = "fixed";
      inputs.style.marginBottom = "100px";
    } else {
      buttons.style.position = "sticky";
      inputs.style.marginBottom = "0px";
    }
  }

  const onInnerSizeIncreasing = () => {
    const buttons = buttonsRef.current;
    const inputs = inputsRef.current;

    if (!inputs || !buttons) return;

    const distance = (buttons.getBoundingClientRect().top - inputs.getBoundingClientRect().top)

    if (window.innerHeight - buttons.getBoundingClientRect().bottom > 10) {
      inputs.style.maxHeight = '0';
    } else {
      inputs.style.maxHeight = distance + "px"
    }
  }

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.code == 'Enter') {
      findHandler();
    }
  }

  const onSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchText(e.target.value)
  }

  return(
    <Styles>
        <AdminNavbar />
        <div className="inner-tab-container">
            <div className="d-flex flex-column"
                style={{borderBottom: "1px solid #D1D6DC", gap: "20px", marginBottom: "24px", paddingBottom: "24px"}}
            >
            <h1 style={{ marginTop:'28px' }}> Вакансии </h1>
            <InputForSearch
                placeholder="Профессия, должность, компания"
                value={searchText}
                onChange={onSearch}
                onKeyDown={onKeyDown}
                size="large"
                prefix={<img src={loupe}/>}
            />
            </div>
        <div>
          <div className="filter-container d-table-row d-md-table-cell">
            <Form style={{width: '100%'}}>
              <div className="inputs-container" ref={inputsRef}>
                <div className="form-group">
                  <Form.Label style={{marginBottom: '2px'}}> Область </Form.Label>
                  <AutoComplete
                    style={{width: '100%', height: '40px'}}
                    options={regionsList}
                    value={region}
                    onClick={getRegions}
                    onChange={regionHandler}
                    onSelect={e => addTag(e, selectedRegions, setSelectedRegions, setRegion)}
                    filterOption={(inputValue, option) =>
                      option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                  />
                  {selectedRegions.map(region => (
                    <StyledTag key={region.id}
                         closable
                         onClose={e => closeTag(e, region.name, selectedRegions, setSelectedRegions)}
                         title={region.name}
                    >
                      <span>{region.name}</span>
                    </StyledTag>
                  ))}
                </div>
                <div className="form-group">
                  <Form.Label style={{marginBottom: '2px'}}> Город </Form.Label>
                  <AutoComplete
                    style={{width: '100%', height: '40px'}}
                    options={citiesList}
                    value={city}
                    onClick={getCities}
                    onChange={e => cityHandler(e)}
                    onSelect={e => addTag(e, selectedCities, setSelectedCities, setCity)}
                    filterOption={(inputValue, option) =>
                      option!.value.toUpperCase().indexOf(inputValue.toUpperCase()) !== -1
                    }
                  />
                  {selectedCities.map(city => (
                    <StyledTag key={city.id}
                         closable
                         onClose={e => closeTag(e, city.name, selectedCities, setSelectedCities)}
                         title={city.name}>
                      {city.name}
                    </StyledTag>
                  ))}
                </div>
                <Checkboxes>
                  <Form.Label style={{marginBottom: '8px'}}> Образование </Form.Label>
                  <div className="checkboxes-group">
                    <Checkbox
                      checked={isHighEducation}
                      onClick={() => setIsHighEducation(!isHighEducation)}
                    >
                      Высшее
                    </Checkbox>
                    <Checkbox
                      checked={isSecondaryVocationalEducation}
                      onClick={() => setIsSecondaryVocationalEducation(!isSecondaryVocationalEducation)}
                    >
                      СПО
                    </Checkbox>
                  </div>
                </Checkboxes>
                <Checkboxes>
                  <Form.Label style={{marginBottom: '8px'}}> Я ищу </Form.Label>
                  <div className="checkboxes-group">
                    <Checkbox
                      checked={isSearchingForTraineeship}
                      onClick={() => setIsSearchingForTraineeship(!isSearchingForTraineeship)}
                    >
                      Стажировку
                    </Checkbox>
                    <Checkbox
                      checked={isSearchingForJob}
                      onClick={() => setIsSearchingForJob(!isSearchingForJob)}
                    >
                      Работу
                    </Checkbox>
                  </div>
                </Checkboxes>
              </div>
              <div className="filter-buttons" ref={buttonsRef}>
                <div className="different-edges-container"
                     style={{paddingTop: '12px', borderTop: '0.5px solid #D1D6DC'}}>
                  <Button type="primary" onClick={findHandler} style={{height: '32px'}}> Найти </Button>
                  <div className="right-edge">
                    <Button type="text" onClick={resetHandler} style={{height: '32px'}}> Сбросить </Button>
                  </div>
                </div>
              </div>
            </Form>
          </div>
          <div className="vacancies-container d-table-row d-md-table-cell">
            {(vacancies.length === 0 && isLoaded) && <p style={{paddingTop:'100px', paddingLeft: '40px'}}> Ничего не найдено. Попробуйте изменить критерии поиска. </p>}
            {vacancies && vacancies.map(vacancy => (
              <VacancyCardEmployer
                vacancy={vacancy}
                key={vacancy.id}
              />
            ))}
            <div>
              {(totalCount > vacancyPageSize && isLoaded) &&
                <Pagination
                  current={page}
                  total={totalCount}
                  onChange={pageHandler}
                  showSizeChanger={false}
                  pageSize={vacancyPageSize}
                  style={{marginLeft:'30%', marginRight:'auto'}}
                />
              }
            </div>
          </div>
        </div>
      </div>
    </Styles>
  )
}